import { Injectable } from '@angular/core';
import { BookingType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import {
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { BookingAirToolsService } from "./booking-air-tools.service";
import { AirBookingResourceService } from "libraries/service/resources/air-booking/air-booking-resource.service";
import { ISashaConfigServices, wenji } from "libraries/interfaces/IWenji";
import { Observable, of, Subject, throwError } from "rxjs";
import {
    AirServicesResourceService
} from "libraries/service/resources/air-services-resource/air-services-resource.service";
import {
    ResponseForConvertSearchAddOnAfx2jsonType
} from "@afidium/ts-facade/model/responseForConvertSearchAddOnAfx2jsonType";
import { HostCodeTargetType } from "@afidium/ts-facade/model/hostCodeTargetType";
import { ServiceInServiceSearchAddon } from "@afidium/ts-facade/model/serviceInServiceSearchAddon";
import { XCategoryForServiceType } from "@afidium/ts-facade/model/xCategoryForServiceType";
import { XApplyChargeableFreeType } from "@afidium/ts-facade/model/xApplyChargeableFreeType";
import { Yii } from "libraries/interfaces/Yii";
import { map, mergeMap } from "rxjs/operators";
import { ServiceXCategoryType } from '@afidium/ts-facade';
import { ServiceInTripType } from "@afidium/ts-facade/model/serviceInTripType";

@Injectable({
    providedIn: 'root'
})
export class BookingAddServicesService {
    constructor(
        private readonly bookingAirToolsService: BookingAirToolsService,
        private readonly airBookingResourceService: AirBookingResourceService,
        private readonly airServicesResourceService: AirServicesResourceService
    ) {
    }

    public addServices(booking?: TGetBookingResponse, bookingWithServices?: any): Observable<ResponseForConvertSearchAddOnAfx2jsonType> {
        console.log({booking, bookingWithServices});
        if (!booking && !bookingWithServices) {
            throw new Error(Yii.t('wui-shared-js', 'Les paramètres de dossier ou de services sont nécessaires pour poursuivre l\'opération.'));
        }
        // https://projets2.afidium.com/issues/36131
        // always do the airb/booking rq, to retrieve the long syscode
        const bookingWithServices$: Observable<any> = this.getBookingWithServices(booking);
        // const bookingWithServices$: Observable<any> = bookingWithServices ? of(bookingWithServices) : this.getBookingWithServices(booking);

        if (bookingWithServices$ !== undefined) {
            return bookingWithServices$.pipe(
                mergeMap((bookingResponse: any): Observable<ResponseForConvertSearchAddOnAfx2jsonType> => {
                    if (bookingResponse) {
                        return this.checkSearchAddOnsParametersAndDoRequest(bookingResponse);
                    }
                    throw new Error(Yii.t('wui-shared-js', 'Impossible de récupérer le dossier au bon format pour procéder à la recherche des services.'));
                }),
            );
        }
        throw new Error(Yii.t('wui-shared-js', 'Impossible de récupérer le dossier au bon format pour procéder à la recherche des services.'));
    }

    private checkSearchAddOnsParametersAndDoRequest(bookingResponse: any): Observable<ResponseForConvertSearchAddOnAfx2jsonType> {
        const bookingSyscode: string = bookingResponse.booking?.syscode?.value;
        const bookingHost: HostCodeTargetType = bookingResponse.booking?.host;
        if (!bookingSyscode || !bookingHost) {
            throw new Error('Données insuffisantes pour rechercher les services.');
        }
        const servicesArray: ServiceInServiceSearchAddon[] = this.getServicesArrayFromConfig();
        if (!servicesArray || !servicesArray.length) {
            throw new Error('Configuration incomplète ou manquante pour procéder à la recherche des services.');
        }
        return this.airServicesResourceService
            .searchAddOns(bookingSyscode, bookingHost, servicesArray)
            .pipe(
                map((response: ResponseForConvertSearchAddOnAfx2jsonType): ResponseForConvertSearchAddOnAfx2jsonType => {
                    return this.modifySearchAddOnnResponse(response);
                })
            );
    }

    private modifySearchAddOnnResponse(response: ResponseForConvertSearchAddOnAfx2jsonType): ResponseForConvertSearchAddOnAfx2jsonType {

        const seatCategoryInDictionary: ServiceXCategoryType = response.dictionary?.serviceXCategoryArray?.length &&
            response.dictionary.serviceXCategoryArray.find((item: ServiceXCategoryType): boolean => item.xCategory === "Seat");

        if (Boolean(seatCategoryInDictionary)) {
            return response;
        }

        const serviceXCategoryArray: ServiceXCategoryType[] = [
            ...response.dictionary.serviceXCategoryArray,
            {
                afid: "SVCXCAT_Seat",
                xCategory: "Seat",
                xScope: "Sector",
                xChoiceOfServices: "Single"
            }
        ];
        response.dictionary.serviceXCategoryArray = serviceXCategoryArray;

        // #36131 : remove notApplicable services
        const applicableServices: ServiceInTripType[] = response.trip?.serviceArray.length
            ? response.trip.serviceArray.filter((serviceItem: ServiceInTripType): boolean => {
                return serviceItem.xApply !== "NotApplicable";
            })
            : null;

        if(applicableServices){
            response.trip.serviceArray = applicableServices;
        }

        return response;
    }

    private getBookingWithServices(booking: TGetBookingResponse): Observable<any> {
        if (booking &&
            booking.type &&
            booking.type.some((type: BookingType): boolean => type === BookingType.air)) {
            return this.airBookingResourceService.getWithServiceSelection(booking.host.toUpperCase(), booking.syscode, wenji.ui.target, true);
        }
        return undefined;
    }

    private getServicesArrayFromConfig(): ServiceInServiceSearchAddon[] {
        const servicesConfig: ISashaConfigServices = wenji.sasha.services;
        let servicesArray: ServiceInServiceSearchAddon[] = [];
        if (!servicesConfig) {
            return [];
        }

        if (servicesConfig.chargeable &&
            servicesConfig.chargeable?.enabled &&
            servicesConfig.chargeable?.xCategories &&
            servicesConfig.chargeable?.xCategories?.length) {
            servicesArray = servicesConfig.chargeable?.xCategories.map((xCategory: string): ServiceInServiceSearchAddon => ({
                xCategory: xCategory as XCategoryForServiceType,
                xApply: "Chargeable" as XApplyChargeableFreeType
            }));
        }

        if (servicesConfig.free &&
            servicesConfig.free?.codes &&
            servicesConfig.free?.codes?.length) {
            servicesArray.push({
                xApply: "Free" as XApplyChargeableFreeType,
                ssr: {
                    iataArray: servicesConfig.free?.codes
                }
            });
        }

        return servicesArray;
    }

}
